import { spacing } from '../spacing'
import { type ComponentCreation } from './types'
import type { Tokens } from '../augmentation'

/**
 * Modal is a lower-level construct that is leveraged by the following components:
 *
 * * [Dialog](https://mui.com/material-ui/api/dialog/)
 * * [Drawer](https://mui.com/material-ui/api/drawer/)
 * * [Menu](https://mui.com/material-ui/api/menu/)
 * * [Popover](https://mui.com/material-ui/api/popover/)
 */
export const createMuiModal = ({ variant, misc }: Pick<Tokens, 'variant' | 'misc'>) =>
  ({
    MuiModal: {
      styleOverrides: {
        root: ({ theme: { breakpoints } }) => [
          {
            '&.DhlModal-container': {
              display: 'flex',
              padding: spacing(3),
              backfaceVisibility: 'hidden',

              '& > .MuiPaper-root': {
                margin: 'auto',

                // Do not show an outline on the paper element itself when focused,
                // e.g., autoFocus with a focus trap
                '&.DhlModal-paper:focus, &.DhlModal-paper:focus-visible': {
                  outline: 'none',
                },

                '& .DhlModal-base': {
                  // Style any parent (e.g., form) that is wrapped around DhlModal-* elements
                  '& > :only-child:not(.DhlModal-content)': {
                    display: 'flex',
                    flex: 1,
                    flexDirection: 'column',
                    height: 0,
                  },

                  '& .DhlModal-header': {
                    padding: spacing(2, 3),
                    gap: spacing(2),
                  },
                  '& .DhlModal-content': {
                    padding: spacing(0, 3),
                    position: 'relative',

                    // Modal with only content, header and footer not defined
                    '&:only-child': {
                      padding: spacing(3),
                    },

                    '&.no-side-padding': {
                      paddingLeft: 0,
                      paddingRight: 0,
                    },
                  },
                  '& .DhlModal-footer': {
                    padding: spacing(2, 3, 2),
                    gap: spacing(2),
                  },

                  '&': variant !== 'mobile' && {
                    '&.DhlModal-dividers-top': {
                      '& .DhlModal-header': {
                        borderBottom: misc.borderStyle('divider'),
                        padding: spacing(2, 3),
                      },
                      '& .DhlModal-content': {
                        paddingTop: spacing(3),
                      },
                    },
                    '&.DhlModal-dividers-bottom': {
                      '& .DhlModal-footer': {
                        borderTop: misc.borderStyle('divider'),
                        padding: spacing(2, 3),
                      },
                      '& .DhlModal-content': {
                        paddingBottom: spacing(3),
                      },
                    },
                  },

                  '&:not(.DhlModal-dividers-bottom)': {
                    // Modal with only header and content, footer not defined
                    '& .DhlModal-header': {
                      '& + .DhlModal-content:last-of-type': {
                        paddingBottom: spacing(3),
                      },
                    },
                  },

                  '&:not(.DhlModal-dividers-top)': {
                    '& .DhlModal-content': {
                      // Modal with only content and footer, header not defined
                      '&:first-of-type:has(+ .DhlModal-footer)': {
                        paddingTop: spacing(3),
                      },
                    },
                  },
                },
              },

              '&.DhlModal-fullscreen': {
                padding: 0,

                '& > .MuiPaper-root': {
                  borderRadius: 0,
                  width: '100%',
                  height: '100%',
                },
              },
              ...(!['xl', 'lg', 'md'].includes(variant)
                ? {
                    '&:not(.DhlModal-fullscreen) > .DhlModal-paper': {
                      [breakpoints.up('xxl')]: {
                        width: '30%',
                      },
                      [breakpoints.between('xl', 'xxl')]: {
                        width: '40%',
                      },
                      [breakpoints.between('lg', 'xl')]: {
                        width: '60%',
                      },
                      [breakpoints.between('md', 'lg')]: {
                        width: '81%',
                      },
                      [breakpoints.between('sm', 'md')]: {
                        width: '87%',
                      },
                      [breakpoints.down('sm')]: {
                        width: '100%',
                      },
                    },
                  }
                : {
                    '&:not(.DhlModal-fullscreen) > .DhlModal-paper': {
                      width: '95vw',
                    },
                  }),
            },
          },
        ],
      },
    },
  }) satisfies ComponentCreation
